import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import RefreshIcon from '@mui/icons-material/Refresh';
import ForumIcon from '@mui/icons-material/Forum';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import InfoIcon from '@mui/icons-material/Info';

import DataTable from '../../../sharedModules/dataTable';
import ProjectCommunicationsFilter from '../components/ProjectCommunicationsFilter';

import { ProjectCommunication } from '../../model';
import { projectCommunicationsColumns } from '../constants/projectCommunicationsColumns';

import axios from '../../../sharedModules/utils/axios.utils';

const defaultFilterValues = {
  db_location_eq: 'all',
  department_eq: 'all',
  status_eq: 'all',
};

const ProjectCommunicationsListPage: React.FunctionComponent = () => {
  const history = useHistory();

  const [projectCommunications, setProjectCommunications] = useState<ProjectCommunication[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [syncing, setSyncing] = useState<boolean>(false);
  const [filterValues, setFilterValues] = useState<any>(defaultFilterValues);
  const [stats, setStats] = useState<any>(null);
  const [ageFilter, setAgeFilter] = useState<string | null>(null);
  const [statsExpanded, setStatsExpanded] = useState<boolean>(true);
  const [showNoRecordDialog, setShowNoRecordDialog] = useState<boolean>(false);

  const fetchProjectCommunications = async (fetchFilterValues = filterValues, fetchAgeFilter = ageFilter) => {
    setLoading(true);
    try {
      const params: any = {
        'ransack[db_location_eq]':
          fetchFilterValues.db_location_eq !== 'all' ? fetchFilterValues.db_location_eq : undefined,
        'ransack[department_eq]':
          fetchFilterValues.department_eq !== 'all' ? fetchFilterValues.department_eq : undefined,
        'ransack[status_eq]': fetchFilterValues.status_eq !== 'all' ? fetchFilterValues.status_eq : undefined,
      };

      if (fetchAgeFilter === 'not_contacted') {
        params['ransack[age_eq]'] = 0;
      } else if (fetchAgeFilter === 'age_0_1_week') {
        params['ransack[age_gt]'] = 0;
        params['ransack[age_lteq]'] = 7;
      } else if (fetchAgeFilter === 'age_1_2_weeks') {
        params['ransack[age_gt]'] = 7;
        params['ransack[age_lteq]'] = 14;
      } else if (fetchAgeFilter === 'age_2_3_weeks') {
        params['ransack[age_gt]'] = 14;
        params['ransack[age_lteq]'] = 21;
      } else if (fetchAgeFilter === 'age_3_4_weeks') {
        params['ransack[age_gt]'] = 21;
        params['ransack[age_lteq]'] = 28;
      } else if (fetchAgeFilter === 'age_4_plus_weeks') {
        params['ransack[age_gt]'] = 28;
      }

      const response = await axios.get<string, any>('/customer_issues/servman_project_communications', {
        params,
      });
      setProjectCommunications(response?.data?.result || []);

      await fetchStats(fetchFilterValues);
    } catch (error) {
      console.error('Error fetching project communications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async (fetchFilterValues = filterValues) => {
    try {
      const params: any = {
        'ransack[db_location_eq]':
          fetchFilterValues.db_location_eq !== 'all' ? fetchFilterValues.db_location_eq : undefined,
        'ransack[department_eq]':
          fetchFilterValues.department_eq !== 'all' ? fetchFilterValues.department_eq : undefined,
        'ransack[status_eq]': fetchFilterValues.status_eq !== 'all' ? fetchFilterValues.status_eq : undefined,
      };

      const response = await axios.get<string, any>('/customer_issues/servman_project_communications/stats', {
        params,
      });
      setStats(response?.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post('/customer_issues/servman_project_communications/sync');
      await fetchProjectCommunications();
      await fetchStats();
      alert('Project communications synced successfully!');
    } catch (error) {
      console.error('Error syncing project communications:', error);
      alert('Failed to sync project communications. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchProjectCommunications(defaultFilterValues);
    fetchStats();
  }, []);

  const handleRowClick = (row: ProjectCommunication) => {
    if (row.recno === '0') {
      setShowNoRecordDialog(true);
    } else {
      history.push(`/project_communications/${row.recno}`);
    }
  };

  const columns = projectCommunicationsColumns(handleRowClick);

  const handleAgeFilterClick = (filterKey: string | null) => {
    setAgeFilter(filterKey);
    fetchProjectCommunications(filterValues, filterKey);
  };

  const handleCloseDialog = () => {
    setShowNoRecordDialog(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ForumIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
                Project Communications
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Track and manage project-related communications with customers
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={syncing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
            onClick={handleSync}
            disabled={syncing}
            sx={{
              px: 3,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4,
              },
            }}
          >
            {syncing ? 'Syncing...' : 'Sync from ServMan'}
          </Button>
        </Box>
        <Divider />
      </Box>

      <ProjectCommunicationsFilter
        filterValues={filterValues}
        setFilterValues={setFilterValues}
        onFilter={fetchProjectCommunications}
      />

      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: 2,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: 'primary.lighter',
                      display: 'flex',
                      mr: 2,
                    }}
                  >
                    <ForumIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="h3" fontWeight="bold" color="primary.main">
                      {stats.total}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  onClick={() => setStatsExpanded(!statsExpanded)}
                  sx={{
                    transition: 'transform 0.3s',
                    transform: statsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                  }}
                >
                  <ExpandMoreIcon />
                </IconButton>
              </Box>
              <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 2 }}>
                Total Communications
              </Typography>

              <Collapse in={statsExpanded}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {[
                    { label: 'Not Contacted', count: stats.not_contacted, color: '#9e9e9e', key: 'not_contacted' },
                    { label: '0-1 Week', count: stats.age_0_1_week, color: '#4caf50', key: 'age_0_1_week' },
                    { label: '1-2 Weeks', count: stats.age_1_2_weeks, color: '#8bc34a', key: 'age_1_2_weeks' },
                    { label: '2-3 Weeks', count: stats.age_2_3_weeks, color: '#ffc107', key: 'age_2_3_weeks' },
                    { label: '3-4 Weeks', count: stats.age_3_4_weeks, color: '#ff9800', key: 'age_3_4_weeks' },
                    { label: '4+ Weeks', count: stats.age_4_plus_weeks, color: '#f44336', key: 'age_4_plus_weeks' },
                  ].map((item) => {
                    const maxCount = Math.max(
                      stats.not_contacted,
                      stats.age_0_1_week,
                      stats.age_1_2_weeks,
                      stats.age_2_3_weeks,
                      stats.age_3_4_weeks,
                      stats.age_4_plus_weeks,
                    );
                    const percentage = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
                    const isActive = ageFilter === item.key;

                    return (
                      <Box
                        key={item.label}
                        onClick={() => handleAgeFilterClick(isActive ? null : item.key)}
                        sx={{
                          cursor: 'pointer',
                          p: 1,
                          borderRadius: 1,
                          transition: 'all 0.2s',
                          border: '2px solid',
                          borderColor: isActive ? item.color : 'transparent',
                          bgcolor: isActive ? `${item.color}10` : 'transparent',
                          '&:hover': {
                            bgcolor: `${item.color}15`,
                            transform: 'translateX(4px)',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={isActive ? 'bold' : 'normal'}
                          >
                            {item.label}
                          </Typography>
                          <Typography variant="caption" fontWeight="bold">
                            {item.count}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: '100%',
                            height: 20,
                            bgcolor: '#f5f5f5',
                            borderRadius: 1,
                            position: 'relative',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              width: `${percentage}%`,
                              height: '100%',
                              bgcolor: item.color,
                              transition: 'width 0.3s ease',
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Collapse>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent sx={{ p: 0 }}>
          <DataTable
            columns={columns}
            data={projectCommunications}
            onRowClicked={handleRowClick}
            progressPending={loading}
            highlightOnHover
            pointerOnHover
            pagination
            paginationPerPage={25}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            dense
          />
        </CardContent>
      </Card>

      <Dialog open={showNoRecordDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="info" />
          Communication Record Not Available
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This project communication does not exist yet in the system.
          </Alert>
          <Typography variant="body1" color="text.secondary">
            The ability to start a new communication record is still under development. Please check back later for this
            feature.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectCommunicationsListPage;
